
import { Options, Vue, prop } from "vue-class-component";
import debounce from "lodash/debounce";
import { UserController } from "@/app/ui/controllers/UserController";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { OptionsClass } from "@/domain/entities/MainApp";
import { RequestHubList, Hub } from "@/domain/entities/Hub";
import { HubController } from "@/app/ui/controllers/HubController";

class Prop {
  isDisabled = prop<boolean>({
    type: Boolean,
    default: false
  });
}

@Options({
  emits: ["opened"],
  components: {},
  directives: {
    "click-outside": {
      beforeMount: function(el, binding) {
        const ourClickEventHandler = (event: any) => {
          if (!el.contains(event.target) && el !== event.target) {
            binding.value(event);
          }
        };
        el.__vueClickEventHandler__ = ourClickEventHandler;
        document.addEventListener("click", ourClickEventHandler);
      },
      unmounted: function(el) {
        document.removeEventListener("click", el.__vueClickEventHandler__);
      }
    }
  }
})
class AdvanceFilterUserManagement extends Vue.with(Prop) {
  isChevron = false;
  openFilter = false;
  divisionName: any = "";
  loadingDivision = false;

  get roleAccount() {
    return (
      AccountController.accountData.account_type_detail.type ||
      AccountController.accountData.account_type
    );
  }
  get isClientParent() {
    return AccountController.accountData.account_type_detail.parentId === 0;
  }

  resetFilter() {
    this.typeValue = "";
    this.referenceModelValue = "";
    this.statusName = "";
    this.hubName = new OptionsClass();
    UserController.resetFilter();
  }

  get sumFilter() {
    return (
      (this.typeValue !== "" ? 1 : 0) +
      (this.referenceModelValue !== "" ? 1 : 0) +
      (this.showHub && this.hubName.value ? 1 : 0) +
      (this.statusName !== "" ? 1 : 0)
    );
  }

  // filter type akun
  typeSelect = false;
  onOpenTypeSelect() {
    this.typeSelect = true;
  }
  onCloseTypeSelect() {
    this.typeSelect = false;
  }
  typeName = "";
  typeValue = "";
  onSelectType(name: string, value: string) {
    this.onCloseTypeSelect();
    this.isReferenceEmptyHub = false;
    if (this.typeValue !== value) {
      this.referenceModelValue = "";
    }
    this.typeValue = value;
    this.typeName = name;
    UserController.selectTypeAction(value);
  }
  get typeData() {
    const selectLabel = this.$t(
      "userManagement.list.filter.selectAccountType"
    ) as string;
    switch (this.roleAccount) {
      case "internal":
        return [
          { name: selectLabel, value: "" },
          { name: "Client Parent", value: "client_parent" },
          { name: "Client Branch", value: "client_branch" },
          { name: "Vendor", value: "vendor" },
          { name: "Console", value: "console" },
          { name: "Sub-Console", value: "sub-console" },
          { name: "POS", value: "pos" },
          { name: "Customer Service", value: "customer-service" }
        ];
      case "console":
        return [
          { name: selectLabel, value: "" },
          { name: "Console", value: "console" }
        ];
      case "sub-console":
        return [
          { name: selectLabel, value: "" },
          { name: "Sub-Console", value: "sub-console" }
        ];
      case "pos":
        return [
          { name: selectLabel, value: "" },
          { name: "POS", value: "pos" }
        ];
      case "client":
        if (this.isClientParent) {
          return [
            { name: selectLabel, value: "" },
            { name: "Client Parent", value: "client_parent" },
            { name: "Client Branch", value: "client_branch" }
          ];
        }
        return [
          { name: selectLabel, value: "" },
          { name: "Client Branch", value: "client_branch" }
        ];
      default:
        return [{ name: selectLabel, value: "" }];
    }
  }

  // filter status
  statusSelect = false;
  get statusData() {
    const selectLabel = this.$t(
      "userManagement.list.filter.selectStatus"
    ) as string;
    return [
      { name: selectLabel, value: "" },
      { name: "Active", value: "active" },
      { name: "Inactive", value: "inactive" }
    ];
  }
  onOpenStatusSelect() {
    this.statusSelect = true;
  }
  onCloseStatusSelect() {
    this.statusSelect = false;
  }
  statusName = "";
  get status() {
    return UserController.status;
  }
  onSelectStatus(name: string, value: string) {
    this.statusName = value;
    const hubId = Number(this.hubName.value);
    UserController.selectStatusAction({ value: value, hubId: hubId });
    this.onCloseStatusSelect();
  }

  // filter reference
  loadingReference = false;
  referenceModelValue: any = "";
  focusListReference() {
    this.loadingReference = true;
    this.fetchListReference("");
  }
  fetchListReference = debounce(async (search: string) => {
    this.loadingReference = true;
    await UserController.getReference(search);
    this.loadingReference = false;
  }, 250);
  get referenceDataList() {
    return UserController.referenceDataList;
  }

  get showHub() {
    return this.typeValue.match(/^console$/);
  }
  isReferenceEmptyHub = false;

  async onSelectReference(value: any) {
    this.isReferenceEmptyHub = false;
    if (value) {
      this.referenceModelValue = value;
      await this.fetchListHub();
      await this.$nextTick();
      this.hubName = new OptionsClass(); // reset hubName
      this.handleHubBySelectReference();
      UserController.selectReferenceAction(value.value);
    } else {
      this.referenceModelValue = "";
      this.hubName = new OptionsClass(); // reset hubName
    }
  }

  handleHubBySelectReference() {
    if (this.listHubDataList.length === 1) {
      this.hubName = this.listHubDataList[0];
    }

    if (!this.listHubDataList.length) {
      this.isReferenceEmptyHub = true;
    }
  }

  // filter hub
  hubPage = 1;
  hubName: OptionsClass = new OptionsClass();
  loadingHub = false;
  listHub: Hub[] = [];
  get listHubDataList() {
    return [
      ...this.listHub.map(
        (hub) =>
          new OptionsClass({
            name: hub.hubName,
            value: hub.hubId.toString()
          })
      )
    ];
  }

  async fetchListHub() {
    if (!this.typeName.match(/^Console$/)) return;
    try {
      this.hubPage = 1;
      this.loadingHub = true;
      const hubList = await HubController.getHubList(
        new RequestHubList({
          page: this.hubPage,
          partnerId: this.referenceModelValue.value,
          city: this.referenceModelValue.partnerCityCode,
          status: "active"
        })
      );

      this.listHub = hubList.data;

      if (this.hubName?.value) {
        const hubId = Number(this.hubName.value);
        const referenceId = this.referenceModelValue.value;

        UserController.selectHubAction({
          type: this.typeValue,
          hub: hubId,
          reference: referenceId
        });
      }
    } catch (err) {
      this.listHub = [];
    } finally {
      this.loadingHub = false;
    }
  }

  async fetchListHubInfiniteScroll() {
    this.hubPage++;
    try {
      this.loadingHub = true;
      const hubList = await HubController.getHubList(
        new RequestHubList({
          page: this.hubPage,
          partnerId: this.referenceModelValue.value,
          city: this.referenceModelValue.partnerCityCode,
          status: "active"
        })
      );

      this.listHub.push(...hubList.data);
    } catch (err) {
      this.listHub = [];
    } finally {
      this.loadingHub = false;
    }
  }

  onSelectHub(value: OptionsClass) {
    if (!value) return (this.hubName = new OptionsClass());
    this.hubName = value;
    const hubId = Number(value.value);
    const referenceId = this.referenceModelValue.value;
    UserController.selectHubAction({
      type: this.typeValue,
      hub: hubId,
      reference: referenceId
    });
  }

  // Field Division
  focusListDivision() {
    this.loadingDivision = true;
    this.fetchListDivision("");
  }
  fetchListDivision = debounce(async (search: string) => {
    this.loadingDivision = true;
    await UserController.getDivision(search);
    this.loadingDivision = false;
  }, 250);

  get divisionDataList() {
    return UserController.divisionDataList.map((item) => ({
      name: item.divisionName,
      value: item.divisionId
    }));
  }
  onSelectDivision(value: any) {
    if (value) {
      this.divisionName = value;
      const referenceId = this.referenceModelValue.value;
      UserController.selectDivisionAction({
        value: value.value,
        reference: referenceId
      });
    }
  }
}
export default AdvanceFilterUserManagement;
